import React, { useState, useEffect, useRef } from 'react';
import API from '../services/api';
import './SiblingPicker.css';

/**
 * Bacı/qardaşları yazarkən mövcud şagirdlər arasından təklif verir.
 *
 * BİR NEÇƏ uşaq əlavə edilə bilər: seçilən hər ad nişan (çip) kimi
 * yuxarıda yığılır, sahə boşalır və növbətisini yazmaq olar.
 * Vergül və ya nöqtəli vergül yazanda da cari ad nişana çevrilir —
 * "Ayan, Murad" kimi yazanlar üçün.
 *
 * Seçim MƏCBURİ deyil: siyahıda tapılmayan ad da nişan kimi saxlanılır
 * (studentId olmadan). Beləliklə hələ sistemə salınmamış uşaq da qeyd
 * edilir, sadəcə sinfi görünmür və sətrinə keçid olmur.
 *
 * value  — vergüllə birləşdirilmiş adlar ("Ayan Qasımova, Murad Əliyev")
 * ids    — vergüllə birləşdirilmiş id-lər ("41,318"); tapılmayan ad üçün boş
 */
export default function SiblingPicker({ value, ids, onChange, className = '', placeholder }) {
  const [draft, setDraft] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timer = useRef(null);

  /* Mətn və id siyahıları eyni sırada saxlanılır: n-ci ad ↔ n-ci id */
  const names = String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  const idList = String(ids || '').split(',').map(s => s.trim());

  const emit = (nextNames, nextIds) => {
    onChange(nextNames.join(', '), nextIds.map(x => x || '').join(','));
  };

  const addChip = (name, id) => {
    const nm = String(name || '').trim();
    if (!nm) return;
    // Eyni şagird iki dəfə əlavə olunmasın
    if (id && idList.includes(String(id))) { setDraft(''); return; }
    if (!id && names.some(x => x.toLowerCase() === nm.toLowerCase())) { setDraft(''); return; }
    emit([...names, nm], [...idList.slice(0, names.length), id ? String(id) : '']);
    setDraft('');
    setResults([]);
    setOpen(false);
  };

  const removeChip = (i) => {
    const nn = names.filter((_, ix) => ix !== i);
    const ni = idList.slice(0, names.length).filter((_, ix) => ix !== i);
    emit(nn, ni);
  };

  // Axtarış yazı dayandıqdan sonra göndərilir — hər hərfdə sorğu atılmır
  useEffect(() => {
    clearTimeout(timer.current);
    const q = draft.trim();
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await API.get('/students/search', { params: { q } });
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [draft]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const onKey = (e) => {
    // Vergül, nöqtəli vergül və Enter cari adı nişana çevirir
    if (e.key === ',' || e.key === ';' || e.key === 'Enter') {
      e.preventDefault();
      addChip(draft, null);
    } else if (e.key === 'Backspace' && !draft && names.length) {
      removeChip(names.length - 1);
    }
  };

  return (
    <div className="sib-wrap" ref={wrapRef}>
      {names.length > 0 && (
        <div className="sib-chips">
          {names.map((nm, i) => (
            <span key={i} className={'sib-chip' + (idList[i] ? ' linked' : '')}>
              {nm}
              {idList[i] && <b className="sib-chip-ok" title="Sistemdəki şagirdə bağlandı">✓</b>}
              <button type="button" className="sib-chip-x" onClick={() => removeChip(i)}>×</button>
            </span>
          ))}
        </div>
      )}

      <div className="sib-field">
        <input
          className={className}
          value={draft}
          placeholder={names.length ? 'Daha bir ad…' : placeholder}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => { if (draft.trim()) addChip(draft, null); }}
          onFocus={() => { if (results.length) setOpen(true); }}
        />
      </div>

      {open && (results.length > 0 || loading) && (
        <div className="sib-drop">
          {loading && <div className="sib-empty">Axtarılır…</div>}
          {!loading && results.map(s => (
            <button key={s.id} type="button" className="sib-opt"
              onMouseDown={e => e.preventDefault()}   /* blur-dan əvvəl işləsin */
              onClick={() => addChip(s.name, s.id)}>
              <span className="sib-opt-name">{s.name}</span>
              <span className="sib-opt-class">{s.class_name || s.grade || '—'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
